import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, fmtUSD, fmtPct, monthName } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import KpiCard from "../components/KpiCard";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
} from "recharts";

function useSitePnl(sid, start, end) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    api.get(`/pnl/site/${sid}?${q.toString()}`).then((r) => setData(r.data));
  }, [sid, start, end]);
  return data;
}

export default function SiteDetail() {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const year = new Date().getUTCFullYear();

  const monthly = useSitePnl(id, `${year}-01-01`, `${year}-12-31`);
  const allTime = useSitePnl(id);
  const thisMonth = useSitePnl(id, `${year}-${String(new Date().getUTCMonth()+1).padStart(2,'0')}-01`, `${year}-${String(new Date().getUTCMonth()+1).padStart(2,'0')}-31`);

  useEffect(() => {
    api.get(`/sites/${id}`).then((r) => setSite(r.data));
  }, [id]);

  const monthlyData = (monthly?.monthly || []).map((m) => {
    const [y, mm] = m.period.split("-");
    return { ...m, label: `${monthName(Number(mm))} ${y.slice(2)}` };
  });

  return (
    <AppShell
      title={site?.name || "Site"}
      subtitle={site ? `PI: ${site.pi_name}${site.clinic_name ? " · " + site.clinic_name : ""}` : ""}
    >
      {!allTime && <div className="text-slate-500">Loading...</div>}
      {allTime && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="pnl-gross-revenue" label="Gross Revenue (All-Time)" value={fmtUSD(allTime.gross_revenue)} accent="#0A4C95" />
            <KpiCard testid="pnl-net-profit" label="Net Profit (All-Time)" value={fmtUSD(allTime.net_profit)} accent="#10B981" />
            <KpiCard testid="pnl-ebitda" label="EBITDA %" value={fmtPct(allTime.ebitda_percent)} accent="#F05D23" />
            <KpiCard testid="pnl-labor-pct" label="Labor % of Revenue" value={fmtPct(allTime.labor_pct_of_revenue)} accent="#F05D23" />
          </div>

          <Tabs defaultValue="monthly" className="w-full">
            <TabsList>
              <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
              <TabsTrigger value="quarterly" data-testid="tab-quarterly">Quarterly</TabsTrigger>
              <TabsTrigger value="annual" data-testid="tab-annual">Annual</TabsTrigger>
              <TabsTrigger value="breakdown" data-testid="tab-breakdown">Cost Breakdown</TabsTrigger>
            </TabsList>

            <TabsContent value="monthly">
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Revenue vs Expenses ({year})</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#0A4C95" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#F05D23" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="quarterly">
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Quarterly Summary ({year})</h3>
                <table className="w-full text-sm stryde-table mt-3">
                  <thead><tr><th className="text-left px-3 py-2">Quarter</th><th className="text-right px-3 py-2">Revenue</th><th className="text-right px-3 py-2">Expenses</th><th className="text-right px-3 py-2">Net</th></tr></thead>
                  <tbody>
                    {[1,2,3,4].map((q) => {
                      const months = monthlyData.filter((m) => {
                        const mm = Number(m.period.split("-")[1]);
                        return mm >= (q-1)*3 + 1 && mm <= q*3;
                      });
                      const rev = months.reduce((s,m) => s + m.revenue, 0);
                      const exp = months.reduce((s,m) => s + m.expenses, 0);
                      return (
                        <tr key={q} className="border-t border-slate-100">
                          <td className="px-3 py-2.5 font-semibold">Q{q} {year}</td>
                          <td className="px-3 py-2.5 text-right font-mono-fin">{fmtUSD(rev)}</td>
                          <td className="px-3 py-2.5 text-right font-mono-fin">{fmtUSD(exp)}</td>
                          <td className="px-3 py-2.5 text-right font-mono-fin font-semibold">{fmtUSD(rev - exp)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="annual">
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Annual Trend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#0A4C95" strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="expenses" stroke="#F05D23" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="breakdown">
              <div className="bg-white border border-border rounded-md p-6 shadow-sm">
                <h3 className="font-semibold">Cost Breakdown (All-Time)</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                  <KpiCard label="PI Distribution" value={fmtUSD(allTime.pi_distribution)} />
                  <KpiCard label="Clinic Hosting" value={fmtUSD(allTime.clinic_fee)} />
                  <KpiCard label="Direct Costs" value={fmtUSD(allTime.direct_costs)} />
                  <KpiCard label="Labor Allocation" value={fmtUSD(allTime.labor_allocation)} />
                  <KpiCard label="Net Profit" value={fmtUSD(allTime.net_profit)} />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </AppShell>
  );
}
